"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { GoogleTagManager } from "@next/third-parties/google";

// Privacy consent gate.
// Google Tag Manager (and therefore GA4 + Google Ads) is NOT loaded until the
// visitor affirmatively opts in. Default state is "denied" (Google Consent Mode v2),
// and the Global Privacy Control (GPC) signal is honored as an opt-out. This means
// zero third-party tracking requests fire before consent.
const STORAGE_KEY = "mh-consent";
const GTM_ID = "GTM-KP98FVTT";

type Choice = "granted" | "denied";

declare global {
  interface Window {
    // dataLayer is already declared by @next/third-parties; only add our own flag.
    __mhGtmLoaded?: boolean;
  }
}

const DENIED: Record<string, string> = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
};

const GRANTED: Record<string, string> = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
};

function pushConsent(command: "default" | "update", state: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(["consent", command, state]);
}

export default function ConsentManager() {
  const [choice, setChoice] = useState<Choice | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Consent Mode v2: everything denied until the visitor decides.
    pushConsent("default", { ...DENIED, wait_for_update: 500 });

    let stored: Choice | null = null;
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === "granted" || v === "denied") stored = v;
    } catch {
      /* localStorage unavailable */
    }

    const gpc =
      typeof navigator !== "undefined" &&
      (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

    if (stored) {
      if (stored === "granted") pushConsent("update", GRANTED);
      setChoice(stored);
    } else if (gpc) {
      // Honor GPC as an opt-out: no tracking, no banner.
      try {
        localStorage.setItem(STORAGE_KEY, "denied");
      } catch {
        /* ignore */
      }
      setChoice("denied");
    } else {
      setShowBanner(true);
    }
    setReady(true);

    const reopen = () => setShowBanner(true);
    window.addEventListener("mh:open-privacy-choices", reopen);
    return () => window.removeEventListener("mh:open-privacy-choices", reopen);
  }, []);

  useEffect(() => {
    if (choice === "granted") window.__mhGtmLoaded = true;
  }, [choice]);

  const apply = useCallback((next: Choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    pushConsent("update", next === "granted" ? GRANTED : DENIED);
    setShowBanner(false);
    // Withdrawing consent after tags already loaded this session — reload to fully stop them.
    if (next === "denied" && window.__mhGtmLoaded) {
      window.location.reload();
      return;
    }
    setChoice(next);
  }, []);

  if (!ready) return null;

  return (
    <>
      {choice === "granted" && <GoogleTagManager gtmId={GTM_ID} />}

      {showBanner && (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby="mh-consent-title"
          aria-describedby="mh-consent-desc"
          className="fixed bottom-0 left-0 right-0 z-[120] p-4 sm:p-6"
        >
          <div className="max-w-3xl mx-auto rounded-2xl border border-[#1a1a1a]/10 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.12)] p-5 sm:p-6">
            <h2 id="mh-consent-title" className="text-base font-semibold text-[#1a1a1a]">
              Your privacy
            </h2>
            <p id="mh-consent-desc" className="mt-2 text-sm leading-relaxed text-[#1a1a1a]/70">
              We use cookies and similar technologies — including Google Analytics and Google
              advertising — to measure site traffic and improve your experience. They stay off until
              you choose. You can accept or decline, and change your choice anytime via{" "}
              <span className="whitespace-nowrap">&ldquo;Your Privacy Choices&rdquo;</span> in the
              footer. See our{" "}
              <Link
                href="/privacy"
                className="text-[#ff6900] underline underline-offset-2 hover:opacity-80"
              >
                Privacy Policy
              </Link>
              .
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={() => apply("denied")}
                className="order-2 sm:order-1 inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-[#1a1a1a]/15 text-sm font-medium text-[#1a1a1a] hover:bg-[#1a1a1a]/5 transition-colors"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={() => apply("granted")}
                className="order-1 sm:order-2 inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-gradient-to-r from-[#ff6900] to-[#c2410c] text-white text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
